import { useContext, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import plur from "plur";
import { Flex, Text, Button } from "@jsq/ds/core";
import { SPACING } from "~/styles/variables";
import { toGlobalId } from "~/utils/graphql-helpers";
import { useAddRoleForm } from "../../UserDetails/components/AddRole/hooks";
import { schema } from "../../UserDetails/components/AddRole/schema";
import { AssignToWorkspaceModal } from "../../UserDetails/components/AssignToWorkspaceModal";
import { UserTableContext } from "../hooks/useSelectRows";
import { AddRoleModal } from "./AddRoleModal";
import { BulkRemoveRolesModal } from "./BulkRemoveRolesModal";
import { useBulkRemoveRolesModal } from "./hooks";
import { RemoveWorkspaceAccessModal } from "./RemoveWorkspaceModal";
import { actionsContainer, container, header } from "./style_vanilla.css";
import { UsersWithoutRolesModal } from "./UsersWithoutRolesModal";

export const BulkActionsPanel = ({
  onClear,
  width,
}: {
  onClear: () => void;
  width: number;
}) => {
  const { users } = useContext(UserTableContext);
  const userIds = useMemo(() => users.map((u) => toGlobalId("UserObjectType", u.id)), [users]);
  const {
    initialValues: initialAddValues,
    onHide: onHideAdd,
    openModal: onAdd,
    showModal: showAdd,
    addCanopyRole,
  } = useAddRoleForm({
    userIds,
  });
  const {
    initialValues: initialRemoveValues,
    onHide: onHideRemove,
    openModal: onRemove,
    showModal: showRemove,
    removeCanopyRole,
    usersWithoutRoles,
    showUsersWithoutRolesModal,
    onHideUsersWithoutRolesModal,
  } = useBulkRemoveRolesModal({
    users,
  });
  const [showAssignWorkspaceModal, setShowAssignWorkspaceModal] = useState(false);
  const [showRemoveWorkspaceModal, setShowRemoveWorkspaceModal] = useState(false);

  const showAssignToWorkspace = () => {
    setShowAssignWorkspaceModal(true);
  };

  const showRemoveWorkspace = () => {
    setShowRemoveWorkspaceModal(true);
  };

  return (
    <Flex flexDirection="column" className={container} style={{ width }}>
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        className={header}
      >
        <Text variant="regular-md">{`${users.length} ${plur("user", users.length)} selected`}</Text>
        <Button onClick={onClear} variant="link" displayAsLink>
          Clear selection
        </Button>
      </Flex>

      <Flex flexDirection="column" gap={SPACING.MEDIUM} className={actionsContainer}>
        <Text variant="secondary-sm">WORKSPACE</Text>
        <Button
          displayAsLink
          onClick={showAssignToWorkspace}
          variant="custom"
          textColor="primary"
          disabled={users.length <= 0}
        >
          Assign to workspace
        </Button>
        <Button
          displayAsLink
          onClick={showRemoveWorkspace}
          variant="custom"
          textColor="primary"
          disabled={users.length <= 0}
        >
          Remove from workspace
        </Button>
      </Flex>

      <Flex flexDirection="column" gap={SPACING.MEDIUM} className={actionsContainer}>
        <Text variant="secondary-sm">CANOPY</Text>
        <Button
          displayAsLink
          onClick={onAdd}
          variant="custom"
          textColor="primary"
          disabled={users.length <= 0}
        >
          Add Canopy role
        </Button>
        <Button
          displayAsLink
          onClick={onRemove}
          variant="custom"
          textColor="primary"
          disabled={users.length <= 0}
        >
          Remove Canopy role
        </Button>
        <UsersWithoutRolesModal
          usersWithoutRoles={usersWithoutRoles}
          showModal={showUsersWithoutRolesModal}
          onHide={onHideUsersWithoutRolesModal}
        />
      </Flex>

      {/* add role form */}
      <Formik
        initialValues={initialAddValues}
        onSubmit={addCanopyRole}
        validationSchema={schema}
      >
        {({ handleSubmit, isSubmitting, resetForm }) => {
          const handleClose = () => {
            resetForm();
            onHideAdd();
          };

          return (
            <Form>
              <AddRoleModal
                showModal={showAdd}
                onHide={handleClose}
                isSubmitting={isSubmitting}
                handleSubmit={handleSubmit}
                count={users.length}
              />
            </Form>
          );
        }}
      </Formik>

      {/* remove role form */}
      <Formik
        initialValues={initialRemoveValues}
        onSubmit={removeCanopyRole}
        validationSchema={schema}
      >
        {({ handleSubmit, isSubmitting, resetForm }) => {
          const handleClose = () => {
            resetForm();
            onHideRemove();
          };

          return (
            <Form>
              <BulkRemoveRolesModal
                handleSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                onHide={handleClose}
                showModal={showRemove}
                userIds={userIds}
              />
            </Form>
          );
        }}
      </Formik>

      <AssignToWorkspaceModal
        isOpen={showAssignWorkspaceModal}
        onClose={() => setShowAssignWorkspaceModal(false)}
        userIds={users.map((u) => u.id)}
      />

      <RemoveWorkspaceAccessModal
        isOpen={showRemoveWorkspaceModal}
        onClose={() => setShowRemoveWorkspaceModal(false)}
        users={users}
      />
    </Flex>
  );
};
